// src/components/gatherings/RescheduleModal.tsx
import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  Divider,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Card,
  CardHeader,
  CardContent
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Import Grid directly as per MUI v7 pattern
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Gathering, GatheringStatus, Space } from '../../../../types';
import { format, parseISO } from 'date-fns';
import TimelineView from '../gathering-detail/TimelineView';
import { updateGatheringSchedule, moveGathering } from '../../../../services/api/endpoints/gatherings';
import { fetchAreasBySpaceId, fetchSpaces, fetchGatheringsByDateAndSpace } from '../../../../services/api/endpoints/spaces';
import { fetchAreaSchedules, AreaSchedule, BookedSlot } from '../../../../services/areaScheduleService';

// Interface for gathering schedule data
interface GatheringScheduleData {
  date: string;
  start_time: string;
  end_time: string;
  duration: number;
  space_id: string;
  area_ids: string[];
}

interface RescheduleModalProps {
  open: boolean;
  onClose: () => void;
  gathering: Gathering;
  onSave: (scheduleData: GatheringScheduleData) => Promise<void>;
  token: string;
}

// Style for the modal content using MUI v7 patterns
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1200px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  overflow: 'auto', // Add overflow auto to enable scrolling
};

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  open,
  onClose,
  gathering,
  onSave,
  token
}) => {
  // State for selected date and time slot
  const [selectedDate, setSelectedDate] = useState<string>(gathering.date || '');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ startTime: string; endTime: string; areaId: string } | null>({
    startTime: gathering.start_time,
    endTime: gathering.end_time,
    areaId: gathering.areas?.[0]?.id || ''
  });
  const [selectedSpace, setSelectedSpace] = useState<string>(gathering.space?.id || '');
  const [selectedArea, setSelectedArea] = useState<string>(gathering.areas?.[0]?.id || '');
  const [selectedDuration, setSelectedDuration] = useState<number>(gathering.duration || 60);
  
  // State for area schedules
  const [areaSchedules, setAreaSchedules] = useState<AreaSchedule[]>([]);
  
  // State for available spaces
  const [spaces, setSpaces] = useState<Space[]>([]);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Disable save button if no time slot is selected
  const isSaveDisabled = !selectedTimeSlot || isSaving;
  
  // Load area schedules when the modal opens or date/space changes
  useEffect(() => {
    if (open && selectedDate && selectedSpace) {
      fetchAreaSchedulesData(selectedDate, selectedSpace);
    }
  }, [open, selectedDate, selectedSpace]);
  
  // Reset selected date and space when modal opens
  useEffect(() => {
    if (open) {
      setSelectedDate(gathering.date || '');
      setSelectedSpace(gathering.space?.id || '');
      setSelectedTimeSlot({
        startTime: gathering.start_time,
        endTime: gathering.end_time,
        areaId: gathering.areas?.[0]?.id || ''
      });
    }
  }, [open, gathering.date, gathering.space?.id]);
  
  // Fetch available spaces when the modal opens
  useEffect(() => {
    if (open) {
      const loadSpaces = async () => {
        try {
          const spacesData = await fetchSpaces(token);
          setSpaces(spacesData);
        } catch (error) {
          console.error('Error fetching spaces:', error);
        }
      };
      
      loadSpaces();
    }
  }, [open, token]);
  
  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = parseISO(dateString);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (timeString: string): string => {
    if (!timeString) return 'N/A';
    
    try {
      // Check if the time already has AM/PM
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }
      
      // Assuming time is in format like "19:00:00"
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      
      return `${formattedHour}:${minutes} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration to display hours and minutes
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins > 0 ? `${mins}m` : ''}` : `${mins}m`;
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime: string, duration: number): string => {
    try {
      // Convert start time to position in hours since midnight
      const startPosition = timeToPosition(startTime) + 8; // Add 8 since timeToPosition returns hours from 8 AM
      
      // Add duration hours
      const endPosition = startPosition + (duration / 60);
      
      // Convert back to time string
      const hour = Math.floor(endPosition);
      const minute = Math.round((endPosition - hour) * 60);
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const period = hour >= 12 ? 'PM' : 'AM';
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('Error calculating end time:', error);
      return startTime;
    }
  };

  // Convert time string to position in hours (copied from TimelineView)
  const timeToPosition = (time: string) => {
    if (time.includes(':')) {
      if (time.includes('AM') || time.includes('PM')) {
        // Format: "7:00 PM"
        const [timeStr, period] = time.split(' ');
        const [hour, minute] = timeStr.split(':').map(Number);
        let hour24 = hour;
        if (period === 'PM' && hour !== 12) hour24 += 12;
        if (period === 'AM' && hour === 12) hour24 = 0;
        
        const startHour = 8; // Timeline starts at 8 AM
        return ((hour24 - startHour) * 60 + minute) / 60; // Position in hours from 8 AM
      } else {
        // Format: "19:00:00"
        const [hour, minute] = time.split(':').map(Number);
        const startHour = 8; // Timeline starts at 8 AM
        return ((hour - startHour) * 60 + minute) / 60; // Position in hours from 8 AM
      }
    }
    return 0;
  };

  // Fetch area schedules from the API
  const fetchAreaSchedulesData = async (date: string, spaceId: string) => {
    setIsLoading(true);
    
    try {
      // Use our new service to fetch area schedules
      const schedules = await fetchAreaSchedules(
        fetchAreasBySpaceId,
        fetchGatheringsByDateAndSpace,
        token,
        date,
        spaceId,
        spaces.find(space => space.id === spaceId)?.name || gathering.space?.name
      );
      setAreaSchedules(schedules);
      
      // If the gathering has areas, pre-select the first one
      if (gathering.areas && gathering.areas.length > 0) {
        setSelectedArea(gathering.areas[0].id);
      }
    } catch (error) {
      console.error('Error fetching area schedules:', error);
      setError('Failed to load area schedules. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle date change
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
    setSelectedTimeSlot(null); // Reset time slot when date changes
  };
  
  // Handle time slot selection
  const handleTimeSlotSelect = (startTime: string, endTime: string, areaId: string) => {
    setSelectedTimeSlot({ startTime, endTime, areaId });
    setSelectedArea(areaId);
  };
  
  // Handle area selection
  const handleAreaChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedArea(event.target.value as string);
  };
  
  // Handle space selection
  const handleSpaceChange = (event: SelectChangeEvent<string>) => {
    const newSpaceId = event.target.value as string;
    setSelectedSpace(newSpaceId);
    setSelectedTimeSlot(null); // Reset time slot when space changes
  };
  
  // Duration options for select dropdown (30-minute increments from 30 min to 12 hours)
  const durationOptions = Array.from({ length: 24 }, (_, i) => (i + 1) * 30);
  
  // Handle duration select change
  const handleDurationSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedDuration(Number(event.target.value));
  };
  
  // Handle save button click
  const handleSave = async () => {
    if (!selectedTimeSlot) return;
    
    // Validate that the selected date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison
    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0);
    
    if (selectedDateObj < today) {
      setError('Cannot schedule gatherings in the past. Please select a future date.');
      return;
    }
    
    setIsSaving(true);
    setError(null); // Clear any existing errors
    
    try {
      // Detect if this is a space change
      const isSpaceChange = selectedSpace !== gathering.space?.id;
      
      if (isSpaceChange) {
        // Use new move endpoint for space changes
        await moveGathering(token, gathering.id, {
          new_space_id: selectedSpace,
          new_area_ids: [selectedTimeSlot.areaId]
        });
      } else {
        // Use existing reschedule endpoint for same-space changes
        const scheduleData: GatheringScheduleData = {
          date: selectedDate,
          start_time: selectedTimeSlot.startTime,
          end_time: calculateEndTime(selectedTimeSlot.startTime, selectedDuration),
          duration: selectedDuration,
          space_id: selectedSpace,
          area_ids: [selectedTimeSlot.areaId]
        };
        
        await updateGatheringSchedule(token, gathering.id, scheduleData);
      }
      
      // Create schedule data for the parent callback (for consistency)
      const scheduleData: GatheringScheduleData = {
        date: selectedDate,
        start_time: selectedTimeSlot.startTime,
        end_time: calculateEndTime(selectedTimeSlot.startTime, selectedDuration),
        duration: selectedDuration,
        space_id: selectedSpace,
        area_ids: [selectedTimeSlot.areaId]
      };
      
      // Call the onSave function passed from the parent component
      await onSave(scheduleData);
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError('Failed to save schedule. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Get the selected area details
  const getSelectedAreaDetails = () => {
    if (!selectedTimeSlot) return null;
    
    const area = areaSchedules.find(a => a.id === selectedTimeSlot.areaId);
    return area;
  };
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="reschedule-modal-title"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography id="reschedule-modal-title" variant="h5" component="h2">
            Reschedule {gathering.title}
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Main content */}
        <Grid container spacing={3}>
          {/* Left column - Current and new schedule info */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Current Schedule */}
            <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Schedule
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {formatDate(gathering.date)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>⏱️</Typography>
                </Box>
                <Typography variant="body1">
                  {formatTime(gathering.start_time)} - {formatTime(gathering.end_time)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {gathering.space?.name || 'Unknown Space'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>👥</Typography>
                </Box>
                <Typography variant="body1">
                  {gathering.event_size || 'Unknown'} attendees
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>⏰</Typography>
                </Box>
                <Typography variant="body1">
                  {formatDuration(gathering.duration || 60)}
                </Typography>
              </Box>
              
              {gathering.areas && gathering.areas.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Areas:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {gathering.areas.map(area => (
                      <Chip 
                        key={area.id} 
                        label={area.name} 
                        size="small" 
                        sx={{ bgcolor: 'background.default' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="duration-select-label">Duration</InputLabel>
                <Select
                  labelId="duration-select-label"
                  id="duration-select"
                  value={String(selectedDuration)}
                  label="Duration"
                  onChange={handleDurationSelectChange}
                >
                  {durationOptions.map(option => (
                    <MenuItem key={option} value={String(option)}>
                      {formatDuration(option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
            
            {/* Selected Schedule */}
            <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                New Schedule
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {formatDate(selectedDate)}
                </Typography>
              </Box>
              
              {selectedTimeSlot ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>⏱️</Typography>
                    </Box>
                    <Typography variant="body1">
                      {formatTime(selectedTimeSlot.startTime)} - {formatTime(calculateEndTime(selectedTimeSlot.startTime, selectedDuration))}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      {spaces.find(space => space.id === selectedSpace)?.name || 'Unknown Space'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>👥</Typography>
                    </Box>
                    <Typography variant="body1">
                      {gathering.event_size || 'Unknown'} attendees
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 24, height: 24, mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>⏰</Typography>
                    </Box>
                    <Typography variant="body1">
                      {formatDuration(selectedDuration)}
                    </Typography>
                  </Box>
                  
                  {getSelectedAreaDetails() && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Area:
                      </Typography>
                      <Chip 
                        label={getSelectedAreaDetails()?.name} 
                        size="small" 
                        sx={{ bgcolor: 'background.default' }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Select a time slot from the timeline
                </Typography>
              )}
            </Paper>
          </Grid>
          
          {/* Right column - Date picker and timeline */}
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Date and Space Selector */}
            <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Date"
                    type="date"
                    fullWidth
                    value={selectedDate}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="space-select-label">Space</InputLabel>
                    <Select
                      labelId="space-select-label"
                      id="space-select"
                      value={selectedSpace}
                      label="Space"
                      onChange={handleSpaceChange}
                    >
                      {spaces.map(space => (
                        <MenuItem key={space.id} value={space.id}>
                          {space.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Daily Schedule View */}
            <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <TimelineView
                date={selectedDate}
                areas={areaSchedules}
                onTimeSlotSelect={handleTimeSlotSelect}
                selectedSlot={selectedTimeSlot}
                gatheringDuration={selectedDuration}
                isLoading={isLoading}
                error={error}
                editingGatheringId={gathering.id}
              />
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSave}
                disabled={isSaveDisabled}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default RescheduleModal;
